import { useState, useEffect } from "react";
import { RedirectUrlType } from "@cede/types";
import { getCexLabel } from "@cede/utils";
import { useSearchParams } from "next/navigation";
import { enqueueSnackbar } from "notistack";

interface RedirectParams {
  error: string | null;
  success: string | null;
  type: RedirectUrlType | null;
  exchangeId: string | null;
  socialName: string | null;
}

const handleOAuthError = (error: string, _exchangeId: string | null) => {
  enqueueSnackbar(error || "Something went wrong", { variant: "error" });
};

const handleSocialError = (error: string, _socialName: string | null) => {
  enqueueSnackbar(error || "Something went wrong", { variant: "error" });
};

const handleOAuthSuccess = (exchangeId: string) => {
  const cexLabel = getCexLabel(exchangeId);
  enqueueSnackbar(`Exchange account for ${cexLabel} was added successfully`, { variant: "success" });
};

export const useTrackRedirectStatus = () => {
  const searchParams = useSearchParams();
  const [hasTrackedRedirectStatus, setHasTrackedRedirectStatus] = useState(false);

  useEffect(() => {
    if (hasTrackedRedirectStatus) return;

    const redirectParams: RedirectParams = {
      error: searchParams.get("error"),
      success: searchParams.get("success"),
      type: searchParams.get("type") as RedirectUrlType | null,
      exchangeId: searchParams.get("exchangeId"),
      socialName: searchParams.get("socialName"),
    };

    const { error, success, type, exchangeId, socialName } = redirectParams;

    // Handle error cases
    if (error) {
      switch (type) {
        case RedirectUrlType.OAUTH:
          handleOAuthError(error, exchangeId);
          break;
        case RedirectUrlType.SOCIAL:
          handleSocialError(error, socialName);
          break;
        default:
          break;
      }
    }

    // Handle success cases
    if (success && exchangeId) {
      handleOAuthSuccess(exchangeId);
    }

    setHasTrackedRedirectStatus(true);
  }, [searchParams, hasTrackedRedirectStatus]);
};
